import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_medx-dashboard-demo/artifacts/8k9vczlq_medx11.png";

export function DashSidebar({ items, basePath, title = "Patient" }) {
    const { pathname, hash } = useLocation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const current = (hash || "#overview").replace("#", "");

    return (
        <aside className="w-64 shrink-0 bg-white border-r border-gray-100 min-h-screen sticky top-0 flex flex-col" data-testid="dash-sidebar">
            <Link to="/" className="flex items-center gap-3 px-5 h-16 border-b border-gray-100">
                <img src={LOGO} alt="Medx AI" className="h-8 w-8 rounded-md object-contain" />
                <div className="leading-none">
                    <div className="font-display text-lg font-bold">medx<span className="text-[var(--medx-orange)]">.</span>ai</div>
                    <div className="text-[9px] tracking-[0.18em] text-gray-500 font-mono">{title.toUpperCase()} APP</div>
                </div>
            </Link>

            <nav className="flex-1 px-3 py-4 space-y-1">
                {items.map((it) => {
                    const isActive = (basePath === pathname) && (current === it.id);
                    const Icon = it.icon;
                    const isDisabled = !!it.disabled;
                    return (
                        <a
                            key={it.id}
                            href={isDisabled ? "#" : `${basePath}#${it.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                if (isDisabled) {
                                    it.onDisabledClick?.();
                                    return;
                                }
                                navigate(`${basePath}#${it.id}`);
                            }}
                            aria-disabled={isDisabled || undefined}
                            title={isDisabled ? it.disabledTitle || "" : undefined}
                            className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isDisabled
                                    ? "text-gray-400 cursor-not-allowed opacity-60 hover:bg-gray-50"
                                    : isActive
                                        ? "bg-gradient-to-r from-[var(--medx-red)]/10 to-[var(--medx-orange)]/10 text-[var(--medx-red)]"
                                        : "text-gray-600 hover:bg-gray-50 hover:text-[var(--medx-red)]"
                            }`}
                            data-testid={`sidebar-${it.id}`}
                        >
                            <Icon className={`w-4 h-4 ${isActive && !isDisabled ? "text-[var(--medx-orange)]" : ""}`} />
                            <span className="flex-1 truncate">{it.label}</span>
                            {isDisabled ? (
                                <span className="text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500" data-testid={`sidebar-${it.id}-offline`}>
                                    OFFLINE
                                </span>
                            ) : it.badge ? (
                                <span
                                    className="ml-auto min-w-[20px] px-1.5 h-5 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold"
                                    data-testid={`sidebar-${it.id}-badge`}
                                >
                                    {it.badge}
                                </span>
                            ) : null}
                        </a>
                    );
                })}
            </nav>

            <div className="border-t border-gray-100 p-4">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold text-sm">
                        {(user?.name || "U").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{user?.name}</div>
                        <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                    </div>
                </div>
                <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[var(--medx-red)] py-2 rounded-lg border border-gray-100 hover:border-red-100"
                    data-testid="sidebar-logout"
                >
                    <LogOut className="w-4 h-4" /> Sign out
                </button>
            </div>
        </aside>
    );
}
